import {
  InformationCircleIcon,
  PlusIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import useAxios from "axios-hooks";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import MemberForm from "./form/InstructorsForm";
import { CreateUserDto, UserResDto, UsersResDto } from "../../api/Api";
import AutocompleteInput from "../../components/input/AutoCompleteInput";

import moment from "moment";
import PaginationButtons from "../../components/pagination/PaginationButtons";
import { UserContext } from "../../App";

export function InstructorsTable({ data }: { data: UserResDto[] | undefined }) {
  const navigate = useNavigate();
  const user = useContext(UserContext);

  return (
    <div className="overflow-x-auto rounded-xl  border border-base-300 bg-base-100 table-container">
      <table className="table table-md w-full">
        <thead className="bg-base-200 sticky top-0 z-10 text-sm">
          <tr>
            <th>#</th>
            <th>Username</th>
            <th>Email</th>
            <th>Classrooms</th>
            <th>Status</th>
            <th className="text-center">Joined</th>
          </tr>
        </thead>
        <tbody>
          {data?.map((member, index) => (
            <tr
              key={member._id}
              className="hover:bg-base-300 transition cursor-pointer"
              onClick={() => {
                if (user["cognito:groups"].includes("BUSINESS_INSTRUCTOR")) {
                  // do something when instructor clicks row?
                } else {
                  navigate(`/instructors/${member._id}/students`);
                }
              }}
            >
              <td>{index + 1}</td>

              <td className="font-medium whitespace-nowrap">
                {member.username}{" "}
                {member.userId === user.sub && (
                  <div className="badge badge-primary">ME</div>
                )}
              </td>

              <td className="text-base-content/70 text-sm whitespace-nowrap">
                {member.email}
              </td>

              <td className="space-y-1">
                {member.classrooms?.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {member.classrooms.map((classroom: any) => (
                      <span
                        key={classroom._id}
                        className="badge badge-sm badge-outline"
                      >
                        {classroom.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-base-content/50 italic">
                    No classrooms
                  </span>
                )}
              </td>

              <td>
                {member.enabled ? (
                  <span className="badge badge-success badge-sm">Active</span>
                ) : (
                  <span className="badge badge-error badge-sm">Disabled</span>
                )}
              </td>

              <td className="text-center text-xs text-base-content/60">
                {moment(member.createdAt).fromNow()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Instructors(props: { classroomId?: string }) {
  const outletContext = useOutletContext() as any;
  const navigate = useNavigate();
  const user = useContext(UserContext);

  const [searchString, setSearchString] = useState(undefined as any);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [{ data, loading, error }, getMembers] = useAxios<UsersResDto>({
    url: "/users",
    params: {
      search: searchString,
      ...(props.classroomId ? { classroomId: props.classroomId } : {}),
      roles: ["BUSINESS_INSTRUCTOR"],
      page,
      limit: pageSize,
    },
  });

  useEffect(() => {
    outletContext?.setTitleComponent(
      <div className="flex items-center justify-center gap-2">
        <UserGroupIcon className="h-8 w-8"></UserGroupIcon>
        Instructors
      </div>
    );
    if (searchString !== undefined) {
      getMembers();
    }
  }, [searchString]);

  return (
    <div className="flex flex-col gap-2 p-5">
      <div className="flex">
        <div className="mb-2">
          <AutocompleteInput
            onComplete={(input: string) => {
              setSearchString(input);
            }}
            placeHolder="Search..."
            className="bg-base-200"
          ></AutocompleteInput>
        </div>
        {user["cognito:groups"].includes("BUSINESS_ADMIN") && (
          <div className="ml-auto">
            <button
              className="btn btn-sm"
              onClick={() => {
                (
                  document?.getElementById("new_member_modal") as any
                )?.showModal();
              }}
            >
              <PlusIcon className="h-4"></PlusIcon>
              New Instructor
            </button>
          </div>
        )}
      </div>
      {!loading ? (
        <div className="flex gap-4 flex-col">
          <InstructorsTable data={data?.docs}></InstructorsTable>
          <PaginationButtons
            page={page}
            setPage={setPage}
            pageCount={data?.totalPages || 1}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </div>
      ) : (
        <div className="h-[20vh] w-full flex items-center justify-center">
          <span className="loading loading-spinner loading-md"></span>
        </div>
      )}

      <dialog id="new_member_modal" className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg pb-2">Create Instructor</h3>
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
              ✕
            </button>
          </form>
          <div>
            <MemberForm
              onSuccess={(user: UserResDto) => {
                navigate(`/instructors/${user?._id}/students`);
              }}
            ></MemberForm>
          </div>
          {/* <div className="alert text-xs italic bg-transparent bg-base-100 border-0 opacity-50 p-0 mt-4">
            <InformationCircleIcon className="h-6"></InformationCircleIcon>
            You will be charged automatically for the new Member and an
            invitaion email with a temporary password will be sent to the member
            that you add.
          </div> */}
        </div>
      </dialog>
    </div>
  );
}
